/* eslint-disable spire/export-styles */
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import {
    ConfigurationType,
} from "@insite/client-framework/Services/ProductServiceV2";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
// import ProductAvailability, { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability"; // Store Custom Code
// import React from "react"; // Store Custom Code
import { connect, ResolveThunks } from "react-redux";

/* Store Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import useOnDemandInventory from "@store/Framework/Common/Hooks/useOnDemandInventory";
import Icon from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AlertTriangleFill from "@store/Library/Components/Icons/AlertTriangle/AlertTriangleFill";
import ProductAvailability, { ProductAvailabilityStyles } from "@store/Overrides/Components/ProductAvailability";
import { statusStyles } from "@store/Themes/common/mixins/status";
import React, { useState } from "react";
import { css } from "styled-components";
/* Store Custom Code - End */

interface OwnProps {
    isProductDetailsPage?: boolean;
    configurationCompleted?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: HasParentProductId) => {
    return {
        includeRealTimeInventoryAndPricingForSeo:
            getSettingsCollection(state).websiteSettings.includeRealTimeInventoryAndPricingForSeo,
        variantTraits: getProductState(state, ownProps.parentProductId).value?.variantTraits, // Store Custom Code
        variantChildren: getVariantChildrenDataView(state, ownProps.parentProductId).value, // Store Custom Code
        productInfosById: state.pages.productDetails.productInfosById, // Store Custom Code
    };
};

const mapDispatchToProps = {
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps & HasProduct & HasParentProductId & HasProduct; // Store Custom Code
/* Store Custom Code - Start */
export interface ProductContextAvailabilityStyles {
    discontinuedMessage?: InjectableCss;
    discontinuedMessageText?: TypographyPresentationProps;
}

export const productContextAvailabilityStyles: ProductContextAvailabilityStyles = {
    discontinuedMessage: {
        css: statusStyles("danger"),
    },
    discontinuedMessageText: {
        css: css`
            width: 100%;
            ${wrapInContainerStyles}
        `,
    },
};
/* Store Custom Code - End */

const ProductContextAvailability = ({
    includeRealTimeInventoryAndPricingForSeo,
    productInfo,
    product,
    isProductDetailsPage,
    configurationCompleted,
    extendedStyles,
    variantTraits, // Store Custom Code
    variantChildren, // Store Custom Code
    productInfosById, // Store Custom Code
}: Props) => {
    const { inventory: defaultInventory, unitOfMeasure, failedToLoadInventory } = productInfo; // Store Custom Code
    // Fetch accurate per-product inventory (shared across components, single API call)
    const onDemandInventory = useOnDemandInventory(product.id);
    const inventoryToUse = onDemandInventory || defaultInventory;
    const availability = inventoryToUse?.inventoryAvailabilityDtos?.find(
        o => o.unitOfMeasure.toLowerCase() === (unitOfMeasure?.toLowerCase() || ""),
    )?.availability || undefined;
    /* Store Custom Code - Start */
    const [canShowDiscontinued, setCanShowDiscontinued] = useState(false);

    React.useEffect(() => {
        if (!variantTraits || variantTraits.length === 0 || !variantChildren) {
            if (
                product.properties.isDiscontinuedCustom &&
                product.properties.isDiscontinuedCustom?.toLowerCase() === "true" &&
                productInfosById &&
                productInfosById[product.id]?.inventory?.qtyOnHand === 0
            ) {
                setCanShowDiscontinued(true);
                return;
            }
            setCanShowDiscontinued(false);
        } else {
            if (!productInfosById) {
                setCanShowDiscontinued(false);
                return;
            }

            const nbChildren = variantChildren.length;
            const nbDiscontinued = variantChildren
                .filter(child => {
                    if (
                        child.properties.isDiscontinuedCustom &&
                        child.properties.isDiscontinuedCustom?.toLowerCase() === "true"
                    ) {
                        return true;
                    }

                    return false;
                })
                .map(child => child.id).length;
            const nbNoStock = variantChildren
                .map(child => child.id)
                .filter(id => productInfosById[id]?.inventory?.qtyOnHand === 0).length;

            if (nbChildren === nbDiscontinued && nbChildren === nbNoStock) {
                setCanShowDiscontinued(true);
                return;
            }
            setCanShowDiscontinued(false);
        }
    }, [productInfosById]);
    if (canShowDiscontinued) {
        return (
            <StyledWrapper {...productContextAvailabilityStyles.discontinuedMessage}>
                <Icon color="danger" src={AlertTriangleFill} />
                <Typography
                    {...productContextAvailabilityStyles.discontinuedMessageText}
                    data-test-selector="availabilityMessage"
                >
                    {translate("Product_Discontinued")}
                </Typography>
            </StyledWrapper>
        );
    }

    if (
        (getIsWebCrawler() && !includeRealTimeInventoryAndPricingForSeo) ||
        product.isVariantParent ||
        (!!product.configurationType &&
            product.configurationType !== ConfigurationType.None &&
            product.configurationType !== ConfigurationType.Fixed &&
            !configurationCompleted)
    ) {
        return null;
    }

    return (
        <ProductAvailability
            productId={product.id}
            trackInventory={product.trackInventory}
            availability={availability}
            unitOfMeasure={unitOfMeasure}
            isProductDetailsPage={isProductDetailsPage}
            failedToLoadInventory={failedToLoadInventory}
            extendedStyles={extendedStyles}
            // Store Custom Code
            isDiscontinuedCustom={product.properties.isDiscontinuedCustom}
            isOrderAsNeeded={product.properties.isOrderAsNeeded}
            isLiquidation={Boolean(product.detail?.name?.trim())}
            qtyOnHand={inventoryToUse?.qtyOnHand}
        />
    );
};

export default withProduct(withParentProductId(connect(mapStateToProps, mapDispatchToProps)(ProductContextAvailability))); // Store Custom Code
